const GENERIC_API_MESSAGES = new Set([
  'An unexpected server error occurred.',
  'Response status code does not indicate success: 500 (Internal Server Error).',
]);

export function getApiErrorMessage(error: unknown, fallback: string): string {
  const payload = getErrorPayload(error);
  const validationMessage = getValidationMessage(payload);

  if (validationMessage) {
    return validationMessage;
  }

  const specificMessage = getFirstText(
    getProperty(payload, 'detail'),
    getProperty(payload, 'message'),
    getProperty(payload, 'title'),
    getProperty(error, 'detail'),
    getProperty(error, 'message'),
    payload,
  );

  if (specificMessage && !GENERIC_API_MESSAGES.has(specificMessage)) {
    return specificMessage;
  }

  const status = getNumericProperty(error, 'status') ?? getNumericProperty(payload, 'status');

  if (status === 0) {
    return `${fallback}. No se pudo conectar con el servidor. Verificá tu conexión e intentá nuevamente.`;
  }

  if (status === 401 || status === 403) {
    return `${fallback}. No tenés permisos para realizar esta acción.`;
  }

  if (status === 409) {
    return `${fallback}. Los datos ingresados entran en conflicto con información existente. Revisá si ya existe un registro similar.`;
  }

  if (status !== null && status >= 500) {
    return `${fallback}. Ocurrió un problema interno. Intentá nuevamente más tarde.`;
  }

  return fallback;
}

export function getApiValidationMessage(error: unknown, fieldName: string): string | null {
  const payload = getErrorPayload(error);
  const errors = getProperty(payload, 'errors');

  if (!isRecord(errors)) {
    return null;
  }

  const fieldErrors = getProperty(errors, fieldName);

  if (Array.isArray(fieldErrors) && typeof fieldErrors[0] === 'string') {
    return fieldErrors[0];
  }

  return null;
}

function getErrorPayload(error: unknown): unknown {
  const directError = getProperty(error, 'error');

  if (directError) {
    return directError;
  }

  const result = getProperty(error, 'result');

  if (result) {
    return result;
  }

  const response = getProperty(error, 'response');

  if (typeof response === 'string' && response.trim()) {
    try {
      return JSON.parse(response);
    } catch {
      return response;
    }
  }

  return error;
}

function getValidationMessage(payload: unknown): string | null {
  const errors = getProperty(payload, 'errors');

  if (!isRecord(errors)) {
    return null;
  }

  for (const value of Object.values(errors)) {
    if (Array.isArray(value) && typeof value[0] === 'string') {
      return value[0];
    }
  }

  return null;
}

function getFirstText(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return null;
}

function getNumericProperty(source: unknown, propertyName: string): number | null {
  const value = getProperty(source, propertyName);
  return typeof value === 'number' ? value : null;
}

function getProperty(source: unknown, propertyName: string): unknown {
  if (!isRecord(source)) {
    return null;
  }

  return source[propertyName] ?? null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
